import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { to12h } from "../lib/format";
import { Button } from "../components/ui/button";
import { TimePicker12h } from "../components/ui/time-picker-12h";
import { Lock, Unlock, Clock, Save, Loader2, Plus, Trash2, Calendar, CalendarCheck, CalendarX } from "lucide-react";
import { toast } from "sonner";

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const TZ_OPTIONS = [
  { value: "America/New_York", label: "Leste dos EUA — New York/Boston (ET)" },
  { value: "America/Chicago", label: "Central dos EUA — Chicago (CT)" },
  { value: "America/Denver", label: "Montanha — Denver (MT)" },
  { value: "America/Phoenix", label: "Arizona — sem horário de verão (MST)" },
  { value: "America/Los_Angeles", label: "Pacífico — Los Angeles (PT)" },
  { value: "America/Anchorage", label: "Alasca — Anchorage (AKT)" },
  { value: "Pacific/Honolulu", label: "Havaí — Honolulu (HST)" },
  { value: "America/Sao_Paulo", label: "Brasil — São Paulo (BRT)" },
];
const emptyRule = () => ({
  id: null,
  label: "",
  date: "",
  weekdays: [],
  time: "06:00",
});

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtDayLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" });
}

export default function ScheduleRelease() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [releaseTime, setReleaseTime] = useState("");
  const [savedTime, setSavedTime] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [savedDate, setSavedDate] = useState("");
  const [rules, setRules] = useState([]);
  const [releasedDates, setReleasedDates] = useState([]);
  const [releaseMode, setReleaseMode] = useState("manual");
  const [visibleDays, setVisibleDays] = useState(1);
  const [savedVisibleDays, setSavedVisibleDays] = useState(1);
  const [timezone, setTimezone] = useState("America/New_York");
  const [savedTimezone, setSavedTimezone] = useState("America/New_York");
  const [togglingDate, setTogglingDate] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/schedule/release-config");
      setReleaseTime(data.release_time || "");
      setSavedTime(data.release_time || "");
      setReleaseDate("");
      setSavedDate("");
      setRules(data.rules || []);
      setReleasedDates(data.released_dates || []);
      setReleaseMode(data.release_mode || "manual");
      setVisibleDays(data.visible_days ?? 1);
      setSavedVisibleDays(data.visible_days ?? 1);
      setTimezone(data.timezone || "America/New_York");
      setSavedTimezone(data.timezone || "America/New_York");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggleDayRelease(date) {
    const isReleased = releasedDates.includes(date);
    setTogglingDate(date);
    try {
      if (isReleased) {
        const { data } = await api.delete(`/schedule/release-day/${date}`);
        setReleasedDates(data.released_dates || []);
        toast.success(`Agenda de ${fmtDayLabel(date)} TRAVADA`);
      } else {
        const { data } = await api.post(`/schedule/release-day`, { date });
        setReleasedDates(data.released_dates || []);
        toast.success(`Agenda de ${fmtDayLabel(date)} LIBERADA`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao alterar");
    } finally {
      setTogglingDate(null);
    }
  }

  async function changeReleaseMode(mode) {
    try {
      await api.patch("/schedule/release-mode", { mode });
      setReleaseMode(mode);
      toast.success(
        mode === "manual"
          ? "Modo MANUAL: você libera cada dia clicando."
          : "Modo AUTOMÁTICO: agenda libera pelo horário configurado.",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const upcomingDays = useMemo(() => {
    const start = todayISO();
    return Array.from({ length: 14 }).map((_, i) => addDays(start, i));
  }, []);

  const next7Days = useMemo(() => {
    const start = todayISO();
    return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  }, []);
  const allNext7Released = useMemo(
    () => next7Days.every((d) => releasedDates.includes(d)),
    [next7Days, releasedDates],
  );

  async function releaseNext7Days() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/schedule/release-bulk", { dates: next7Days });
      setReleasedDates(data.released_dates || []);
      toast.success("Próximos 7 dias LIBERADOS para os colaboradores");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao liberar");
    } finally {
      setBulkBusy(false);
    }
  }

  async function lockNext7Days() {
    setBulkBusy(true);
    try {
      let latest = releasedDates;
      for (const d of next7Days) {
        if (latest.includes(d)) {
          const { data } = await api.delete(`/schedule/release-day/${d}`);
          latest = data.released_dates || [];
        }
      }
      setReleasedDates(latest);
      toast.success("Próximos 7 dias TRAVADOS");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao travar");
    } finally {
      setBulkBusy(false);
    }
  }

  async function saveTimezone(tz) {
    setSaving(true);
    try {
      await api.patch("/schedule/release-config", {
        release_time: releaseTime || null,
        release_date: releaseDate || null,
        timezone: tz,
      });
      setSavedTimezone(tz);
      const lbl = TZ_OPTIONS.find((o) => o.value === tz)?.label || tz;
      toast.success(`Fuso horário salvo: ${lbl}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function save(timeVal, dateVal, daysVal) {
    setSaving(true);
    try {
      const days = Number.isFinite(+daysVal) ? +daysVal : visibleDays;
      const body = {
        release_time: timeVal || null,
        release_date: dateVal || null,
        visible_days: days,
      };
      await api.patch("/schedule/release-config", body);
      setSavedTime(timeVal || "");
      setSavedDate(dateVal || "");
      setSavedVisibleDays(days);
      const human = dateVal
        ? `Agenda do dia ${dateVal} libera às ${to12h(timeVal)}`
        : timeVal
          ? `Agenda libera diariamente às ${to12h(timeVal)} • ${days} dia(s) à frente`
          : "Agenda sempre visível";
      toast.success(human);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function saveRules(nextRules) {
    try {
      const body = (nextRules || []).map((r) => ({
        id: r.id || null,
        label: r.label || "",
        date: r.date || null,
        weekdays: r.weekdays || [],
        time: r.time,
      }));
      const { data } = await api.put("/schedule/release-rules", body);
      setRules(data.rules);
      toast.success("Regras salvas");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  function addRule() {
    setRules([...rules, emptyRule()]);
  }
  function updateRule(idx, patch) {
    setRules(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRule(idx) {
    setRules(rules.filter((_, i) => i !== idx));
  }
  function toggleWeekday(idx, day) {
    const r = rules[idx];
    const wd = new Set(r.weekdays || []);
    if (wd.has(day)) wd.delete(day); else wd.add(day);
    updateRule(idx, { weekdays: Array.from(wd).sort() });
  }

  const isLocked = !!savedTime;
  const dirty =
    (savedTime || "") !== (releaseTime || "") ||
    (savedDate || "") !== (releaseDate || "") ||
    Number(savedVisibleDays) !== Number(visibleDays);

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
          Operação
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Clock size={26} /> Liberação da Agenda
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Defina a hora em que os colaboradores começam a enxergar a agenda do dia
          no app mobile. Antes desse horário, eles veem apenas a mensagem
          "Sua agenda será liberada às 6:00 AM". Útil para evitar que vejam
          atendimentos durante o sono ou em remanejamentos de última hora.
        </p>
      </div>

      {/* === Modo de liberação === */}
      <div className="cm-card p-5 mb-5" data-testid="release-mode-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Lock size={18} className="text-blue-600" />
              Modo de liberação
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              <strong>Manual (recomendado):</strong> você clica em &quot;Liberar&quot; para cada dia. O colaborador só vê as casas dos dias que você liberou.
              <br />
              <strong>Automático:</strong> janela contínua — o colaborador vê hoje e, no horário definido, libera os próximos dias (você escolhe quantos).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
            <button
              type="button"
              data-testid="release-mode-manual"
              onClick={() => changeReleaseMode("manual")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                releaseMode === "manual"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-600 hover:bg-white/50"
              }`}
            >
              ✋ Manual
            </button>
            <button
              type="button"
              data-testid="release-mode-auto"
              onClick={() => changeReleaseMode("auto")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                releaseMode === "auto"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-600 hover:bg-white/50"
              }`}
            >
              ⏰ Automático
            </button>
          </div>
        </div>
      </div>

      {/* === Liberação manual por dia === */}
      {releaseMode === "manual" && (
        <div className="cm-card p-5 mb-6" data-testid="manual-release-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <CalendarCheck size={18} className="text-blue-600" />
                Liberar agenda por dia
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Clique em &quot;Liberar&quot; para que os colaboradores vejam as casas daquele dia.
              </p>
            </div>
            {releasedDates.length > 0 && (
              <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
                <strong>{releasedDates.length}</strong> dia(s) liberado(s)
              </div>
            )}
          </div>
          <div className="mb-4">
            <Button
              data-testid="release-next-7-days-btn"
              onClick={allNext7Released ? lockNext7Days : releaseNext7Days}
              disabled={bulkBusy}
              className={
                allNext7Released
                  ? "bg-slate-600 hover:bg-slate-700 w-full sm:w-auto"
                  : "bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              }
            >
              {bulkBusy ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : allNext7Released ? (
                <CalendarX size={16} className="mr-2" />
              ) : (
                <CalendarCheck size={16} className="mr-2" />
              )}
              {allNext7Released ? "Travar próximos 7 dias" : "Liberar próximos 7 dias"}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="manual-release-days">
            {upcomingDays.map((d) => {
              const released = releasedDates.includes(d);
              const isToday = d === todayISO();
              const busy = togglingDate === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDayRelease(d)}
                  disabled={busy}
                  data-testid={`release-day-${d}`}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                    released
                      ? "border-emerald-400 bg-emerald-50 hover:bg-emerald-100"
                      : "border-slate-200 bg-white hover:border-blue-300"
                  } ${isToday ? "ring-2 ring-blue-300" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {fmtDayLabel(d)}
                      {isToday && <span className="ml-1 text-blue-600">• HOJE</span>}
                    </div>
                    <div className={`text-sm font-bold mt-0.5 ${released ? "text-emerald-700" : "text-slate-700"}`}>
                      {released ? "🔓 Liberada" : "🔒 Travada"}
                    </div>
                  </div>
                  {busy ? (
                    <Loader2 size={16} className="animate-spin text-slate-500" />
                  ) : released ? (
                    <CalendarX size={18} className="text-emerald-600" />
                  ) : (
                    <CalendarCheck size={18} className="text-slate-400" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
            💡 <strong>Como funciona:</strong> Quando você libera um dia, todos os colaboradores envolvidos passam a ver as limpezas daquele dia automaticamente — sem precisar atualizar. Para travar de novo, é só clicar no mesmo botão.
          </div>
        </div>
      )}

      {releaseMode === "auto" && (
        <>
        <div className="cm-card p-6 max-w-xl" data-testid="schedule-release-card">
        <div className="flex items-center gap-3 mb-5">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isLocked ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {isLocked ? <Lock size={22} /> : <Unlock size={22} />}
          </div>
          <div>
            <div className="font-semibold text-slate-900">
              {isLocked ? `Agenda libera às ${to12h(savedTime)}` : "Agenda sempre visível"}
            </div>
            <div className="text-xs text-slate-500">
              {isLocked
                ? "Colaboradores não veem nada antes desse horário."
                : "Colaboradores veem sua agenda assim que abrirem o app."}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-slate-100 rounded-lg">
          <button
            type="button"
            data-testid="release-mode-always"
            onClick={() => {
              if (savedTime) save("", "");
              else { setReleaseTime(""); setReleaseDate(""); }
            }}
            className={`py-2 rounded-md text-sm font-semibold transition-all ${
              !isLocked
                ? "bg-white text-emerald-700 shadow-sm"
                : "text-slate-600 hover:bg-white/50"
            }`}
          >
            🔓 Sempre visível
          </button>
          <button
            type="button"
            data-testid="release-mode-locked"
            onClick={() => {
              if (!releaseTime) setReleaseTime("06:00");
            }}
            className={`py-2 rounded-md text-sm font-semibold transition-all ${
              isLocked || releaseTime
                ? "bg-white text-blue-700 shadow-sm"
                : "text-slate-600 hover:bg-white/50"
            }`}
          >
            🔒 Liberar em horário
          </button>
        </div>

        {(isLocked || releaseTime) ? (
          <>
            <label className="text-sm font-medium text-slate-700">
              Horário de liberação
            </label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <div className="sm:flex-1">
                <TimePicker12h
                  testId="release-time-input"
                  value={releaseTime}
                  onChange={(v) => setReleaseTime(v)}
                />
              </div>
              <Button
                data-testid="release-time-save"
                onClick={() => save(releaseTime, releaseDate)}
                disabled={loading || saving || !dirty || !releaseTime}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Salvar
              </Button>
            </div>

            {/* Quantidade de dias liberados (janela contínua) */}
            <label className="text-sm font-medium text-slate-700 block mt-4">
              Quantidade de dias liberados à frente
            </label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <input
                type="number"
                min={1}
                max={30}
                data-testid="visible-days-input"
                value={visibleDays}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") { setVisibleDays(""); return; }
                  setVisibleDays(Math.min(30, parseInt(v, 10) || 0));
                }}
                onBlur={() => setVisibleDays((d) => Math.max(1, Math.min(30, parseInt(d || "1", 10))))}
                className="cm-input sm:w-32"
                disabled={loading || saving}
              />
              <Button
                data-testid="visible-days-save"
                onClick={() => save(releaseTime, releaseDate, visibleDays)}
                disabled={loading || saving || Number(savedVisibleDays) === Number(visibleDays)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Salvar dias
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              🪟 <strong>Janela contínua:</strong> o colaborador sempre vê <strong>hoje</strong>.
              Depois das <strong>{releaseTime ? to12h(releaseTime) : "—"}</strong> de cada dia,
              entram os <strong>{visibleDays}</strong> dia(s) seguinte(s) — e nunca mais que isso.
              {" "}Ex.: domingo após o horário com <strong>1</strong> dia → aparece segunda;
              com <strong>2</strong> dias → segunda e terça. Casas de dias passados saem sozinhas quando o dia vira.
            </p>

            {/* Fuso horário do negócio */}
            <label className="text-sm font-medium text-slate-700 block mt-4">
              Fuso horário do negócio
            </label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <select
                data-testid="timezone-select"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="cm-input sm:flex-1"
                disabled={loading || saving}
              >
                {TZ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <Button
                data-testid="timezone-save"
                onClick={() => saveTimezone(timezone)}
                disabled={loading || saving || savedTimezone === timezone}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Salvar fuso
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              🌎 O horário de liberação e o "dia de hoje" são calculados neste fuso.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 self-center mr-1">
                Atalhos:
              </span>
              {["06:00", "07:00", "08:00", "09:00"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`release-quick-${t.replace(":", "")}`}
                  onClick={() => { setReleaseTime(t); save(t, releaseDate); }}
                  className="px-3 py-1.5 rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-xs font-semibold text-slate-700"
                >
                  {to12h(t)}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
            ✅ A agenda do dia aparece para o colaborador <strong>assim que ele
            abrir o app</strong>, sem espera.
          </div>
        )}

        <div className="mt-6 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          <strong>Como funciona:</strong> O app do colaborador consulta a cada
          minuto. Ele sempre vê as casas de <strong>hoje</strong>; ao passar do
          horário, entram os próximos dias liberados (até o limite que você
          definiu). Casas de dias que já passaram saem da tela automaticamente
          quando o dia vira — sem precisar atualizar nem fazer login de novo.
        </div>
      </div>

      {/* === Regras especiais === */}
      <div className="cm-card p-5 mt-6" data-testid="release-rules-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-violet-600" />
              <h2 className="font-bold text-slate-900">Regras especiais</h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Sobrescreve a regra padrão acima para datas ou dias específicos.
              Prioridade: <strong>Data específica</strong> &gt; <strong>Dias da semana</strong> &gt; <strong>Padrão diário</strong>.
            </p>
          </div>
          <Button
            data-testid="release-rule-add"
            onClick={addRule}
            variant="outline"
            size="sm"
          >
            <Plus size={14} className="mr-1" /> Nova regra
          </Button>
        </div>

        {rules.length === 0 ? (
          <div className="text-xs italic text-slate-500 border border-dashed border-slate-200 rounded-md p-3">
            Nenhuma regra especial. Use para feriados, fins de semana ou exceções.
          </div>
        ) : (
          <div className="space-y-3" data-testid="release-rules-list">
            {rules.map((r, i) => (
              <div
                key={r.id || i}
                className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                data-testid={`release-rule-${i}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-3">
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                      Etiqueta
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Feriado, Sábado curto"
                      value={r.label}
                      onChange={(e) => updateRule(i, { label: e.target.value })}
                      className="cm-input w-full"
                      data-testid={`rule-label-${i}`}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                      Data específica
                    </label>
                    <input
                      type="date"
                      value={r.date || ""}
                      onChange={(e) =>
                        updateRule(i, {
                          date: e.target.value,
                          weekdays: e.target.value ? [] : r.weekdays,
                        })
                      }
                      className="cm-input w-full"
                      data-testid={`rule-date-${i}`}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 block mb-1">
                      ou Dias da semana
                    </label>
                    <div className="flex gap-1 flex-wrap">
                      {WEEKDAY_LABELS.map((lbl, d) => {
                        const on = (r.weekdays || []).includes(d);
                        const disabled = !!r.date;
                        return (
                          <button
                            key={lbl}
                            type="button"
                            onClick={() => !disabled && toggleWeekday(i, d)}
                            disabled={disabled}
                            data-testid={`rule-wd-${i}-${d}`}
                            className={`px-2 py-1 rounded-md text-[11px] font-bold transition ${
                              on
                                ? "bg-violet-600 text-white"
                                : "bg-white border border-slate-200 text-slate-700 hover:border-violet-300"
                            } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
                          >
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="md:col-span-2 flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                        Hora
                      </label>
                      <TimePicker12h
                        testId={`rule-time-${i}`}
                        value={r.time}
                        onChange={(v) => updateRule(i, { time: v })}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRule(i)}
                      title="Remover regra"
                      data-testid={`rule-remove-${i}`}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              onClick={() => saveRules(rules)}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="release-rules-save"
            >
              <Save size={14} className="mr-1" /> Salvar regras
            </Button>
          </div>
      </div>
      </>)}
    </AdminLayout>
  );
}
